import React from 'react';
import { Code, Database, Cloud, Settings, Zap, Shield, Brain } from 'lucide-react';

const Skills = () => {
  const skillCategories = [
    {
      title: "Gen AI",
      icon: <Zap className="w-8 h-8" />,
      skills: ["LLM Application Development", "Prompt Engineering", "Responsible AI", "OpenAI API", "Function Calling", "Tool Calling", "OpenRouter", "Huggingface", "RAG", "Vector DB & Embeddings", "FAISS", "Pinecone", "Langchain", "Langsmith"],
      color: "from-pink-400 to-pink-600"
    },
    {
      title: "AI Tools & Platforms",
      icon: <Brain className="w-8 h-8" />,
      skills: ["Github Copilot", "Cursor", "Claude", "bolt.new", "emergent.sh", "Wishpr Flow", "ChatGPT", "Perplexity AI", "Google Gemini", "Grok"],
      color: "from-purple-400 to-purple-600"
    },
    {
      title: "Programming Languages",
      icon: <Code className="w-8 h-8" />,
      skills: ["Python", "Java", "HTML", "CSS", "SQL"],
      color: "from-blue-400 to-blue-600"
    },
    {
      title: "Frameworks & Technologies",
      icon: <Settings className="w-8 h-8" />,
      skills: ["FastAPI", "React", "Bootstrap", "REST APIs", "Apache Tomcat", "Weblogic"],
      color: "from-cyan-400 to-cyan-600"
    },
    {
      title: "Tools & Platforms",
      icon: <Cloud className="w-8 h-8" />,
      skills: ["Jupyter Notebook", "Google Colab", "Jenkins", "PowerShell", "JIRA", "Perforce", "Eclipse", "SAP NetWeaver", "Postman", "Visual Studio Code", "Beyond Compare"],
      color: "from-orange-400 to-orange-600"
    },
    {
      title: "Database & SAP Integration",
      icon: <Database className="w-8 h-8" />,
      skills: ["Oracle", "Microsoft SQL Server", "SAP PI", "SAP CPI", "SAP Directory APIs", "SAP SOAP Webservices", "Amdocs Integration Framework", "Amdocs Smart Client Framework", "Amdocs CRM", "Amdocs Ordering"],
      color: "from-green-400 to-green-600"
    }
  ];

  return (
    <section id="skills" className="py-20 bg-slate-900">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Technical <span className="text-blue-400">Skills</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-purple-500 mx-auto mb-8"></div>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Leveraging cutting-edge technologies and methodologies to build scalable, secure, and innovative solutions
          </p>
        </div>

        {/* Skills Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {skillCategories.map((category, index) => (
            <div
              key={index}
              className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 hover:border-blue-500/50 transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-blue-500/10"
            >
              <div className={`inline-flex p-3 rounded-lg bg-gradient-to-r ${category.color} mb-4`}>
                <div className="text-white">
                  {category.icon}
                </div>
              </div>
              
              <h3 className="text-xl font-semibold text-white mb-4">
                {category.title}
              </h3>
              
              <div className="flex flex-wrap gap-2">
                {category.skills.map((skill, skillIndex) => (
                  <span
                    key={skillIndex}
                    className="bg-slate-700/50 text-gray-300 px-3 py-1 rounded-full text-sm border border-slate-600/50 hover:border-blue-500/50 hover:text-blue-400 transition-all duration-300"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Key Achievements */}
        <div className="bg-gradient-to-r from-slate-800/50 to-slate-700/50 border border-slate-600/50 rounded-xl p-8">
          <h3 className="text-2xl font-semibold text-white mb-8 text-center">
            Professional <span className="text-blue-400">Certifications</span>
          </h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-slate-700/50 border border-slate-600/50 rounded-xl p-6 hover:border-blue-500/50 transition-all duration-300">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-blue-400 mb-2">AI Engineering Accelerator Program</h4>
                  <p className="text-gray-300 font-medium mb-1">Outskill</p>
                  <p className="text-gray-400 text-sm mb-3">Issued Sep 2025</p>
                </div>
              </div>
              <div className="mb-4">
                <p className="text-gray-300 text-sm leading-relaxed">Mastered AI Agents, Frameworks and Product development through hands-on application of most advanced tools including Advanced Prompt Engineering, LLM Development, Workflow Automation, RAG Applications, and multimodal AI (image, audio, video).</p>
              </div>
              <div className="mb-4">
                <p className="text-gray-300 text-sm font-medium mb-2">Skills Covered:</p>
                <div className="flex flex-wrap gap-1">
                  {["AI Agents", "XML & JSON Prompting", "RAG Systems", "n8n Automation", "OpenAI APIs", "HuggingFace", "AI Engineering"].map((skill, index) => (
                    <span key={index} className="bg-blue-500/20 text-blue-400 px-2 py-1 rounded text-xs border border-blue-500/30">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-slate-700/50 border border-slate-600/50 rounded-xl p-6 hover:border-blue-500/50 transition-all duration-300">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-blue-400 mb-2">Applied Generative AI Specialization</h4>
                  <p className="text-gray-300 font-medium mb-1">Simplilearn & Purdue University</p>
                  <p className="text-gray-400 text-sm mb-3">Issued Jun 2025</p>
                </div>
              </div>
              <div className="mb-4">
                <p className="text-gray-300 text-sm leading-relaxed">Mastered Generative AI, Prompt Engineering, LLM Application Development, and Gen AI Capabilities.</p>
              </div>
              <div className="mb-4">
                <p className="text-gray-300 text-sm font-medium mb-2">Skills Covered:</p>
                <div className="flex flex-wrap gap-1">
                  {["Generative AI", "Prompt Engineering", "LLM Application Development", "Gen AI Capabilities"].map((skill, index) => (
                    <span key={index} className="bg-purple-500/20 text-purple-400 px-2 py-1 rounded text-xs border border-purple-500/30">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-slate-700/50 border border-slate-600/50 rounded-xl p-6 hover:border-blue-500/50 transition-all duration-300">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-blue-400 mb-2">Structured Vibe Coding with AI Coding Agents</h4>
                  <p className="text-gray-300 font-medium mb-1">LinkedIn</p>
                  <p className="text-gray-400 text-sm mb-3">Issued Sep 2025</p>
                </div>
              </div>
              <div className="mb-4">
                <p className="text-gray-300 text-sm font-medium mb-2">Skills Covered:</p>
                <div className="flex flex-wrap gap-1">
                  {["AI Agents", "Structured Coding", "AI-Powered Development"].map((skill, index) => (
                    <span key={index} className="bg-green-500/20 text-green-400 px-2 py-1 rounded text-xs border border-green-500/30">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-slate-700/50 border border-slate-600/50 rounded-xl p-6 hover:border-blue-500/50 transition-all duration-300">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-blue-400 mb-2">Vibe Coding Fundamentals: Tools and Best Practices</h4>
                  <p className="text-gray-300 font-medium mb-1">LinkedIn</p>
                  <p className="text-gray-400 text-sm mb-3">Issued Sep 2025</p>
                </div>
              </div>
              <div className="mb-4">
                <p className="text-gray-300 text-sm font-medium mb-2">Skills Covered:</p>
                <div className="flex flex-wrap gap-1">
                  {["AI Productivity", "Artificial Intelligence (AI)", "Programming", "AI Software Development"].map((skill, index) => (
                    <span key={index} className="bg-orange-500/20 text-orange-400 px-2 py-1 rounded text-xs border border-orange-500/30">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-slate-700/50 border border-slate-600/50 rounded-xl p-6 hover:border-blue-500/50 transition-all duration-300">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-blue-400 mb-2">Generative AI Mastermind</h4>
                  <p className="text-gray-300 font-medium mb-1">Outskill</p>
                  <p className="text-gray-400 text-sm mb-3">Issued Aug 2025</p>
                </div>
              </div>
              <div className="mb-4">
                <p className="text-gray-300 text-sm font-medium mb-2">Skills Covered:</p>
                <div className="flex flex-wrap gap-1">
                  {["Generative AI", "Prompt Engineering", "ChatGPT", "Claude", "AI Tools"].map((skill, index) => (
                    <span key={index} className="bg-cyan-500/20 text-cyan-400 px-2 py-1 rounded text-xs border border-cyan-500/30">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-slate-700/50 border border-slate-600/50 rounded-xl p-6 hover:border-blue-500/50 transition-all duration-300">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-blue-400 mb-2">Generative AI for Engineer Mastermind</h4>
                  <p className="text-gray-300 font-medium mb-1">Outskill</p>
                  <p className="text-gray-400 text-sm mb-3">Issued Aug 2025</p>
                </div>
              </div>
              <div className="mb-4">
                <p className="text-gray-300 text-sm font-medium mb-2">Skills Covered:</p>
                <div className="flex flex-wrap gap-1">
                  {["Generative AI", "AI Tools", "Prompt Engineering"].map((skill, index) => (
                    <span key={index} className="bg-pink-500/20 text-pink-400 px-2 py-1 rounded text-xs border border-pink-500/30">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-slate-700/50 border border-slate-600/50 rounded-xl p-6 hover:border-blue-500/50 transition-all duration-300">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-blue-400 mb-2">AGS Essentials of GenAI & Prompt Engineering</h4>
                  <p className="text-gray-300 font-medium mb-1">Simplilearn</p>
                  <p className="text-gray-400 text-sm mb-3">Issued May 2025</p>
                </div>
              </div>
              <div className="mb-4">
                <p className="text-gray-300 text-sm font-medium mb-2">Skills Covered:</p>
                <div className="flex flex-wrap gap-1">
                  {["Generative AI", "Prompt Engineering", "ChatGPT"].map((skill, index) => (
                    <span key={index} className="bg-teal-500/20 text-teal-400 px-2 py-1 rounded text-xs border border-teal-500/30">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-slate-700/50 border border-slate-600/50 rounded-xl p-6 hover:border-blue-500/50 transition-all duration-300">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-blue-400 mb-2">Generative AI Landscape</h4>
                  <p className="text-gray-300 font-medium mb-1">Infosys</p>
                  <p className="text-gray-400 text-sm mb-3">Issued Jan 2025</p>
                </div>
              </div>
              <div className="mb-4">
                <p className="text-gray-300 text-sm font-medium mb-2">Skills Covered:</p>
                <div className="flex flex-wrap gap-1">
                  <span className="bg-indigo-500/20 text-indigo-400 px-2 py-1 rounded text-xs border border-indigo-500/30">
                    Generative AI
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Certification Stats */}
          <div className="mt-8 pt-6 border-t border-slate-600/50">
            <div className="grid grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-3xl font-bold text-blue-400 mb-2">8+</div>
                <p className="text-gray-400 text-sm">AI Certifications</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-purple-400 mb-2">2025</div>
                <p className="text-gray-400 text-sm">Recent Achievements</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-green-400 mb-2">GenAI</div>
                <p className="text-gray-400 text-sm">Specialization Focus</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Skills;